import { useMemo } from "react";

import { ArrowDown2 } from "@similarweb/icons";
import { type ColumnsType, Table } from "@similarweb/pro-logic-components";
import { colorsPalettes } from "@similarweb/styles";
import { Flex, Stack, Text } from "@similarweb/ui-components/foundations";

import { abbrNumberFilter } from "app/filters/ngFilters";
import { useTranslation } from "pro-features/components/WithTranslation/src/I18n";

import { useKeywordsBreakdownQuickFilterIntent } from "../../keywords-breakdown-widget/useKeywordsBreakdownQuickFilterIntent";
import { METRIC_CARDS_KEY_PREFIX } from "../shared/constants";
import { MetricCard } from "../shared/MetricCard";
import { scrollToKeywordsBreakdown } from "../shared/scrollToKeywordsBreakdown";

import { getKeywordAnalysisHref } from "./keywordAnalysisHref";
import type { INewKeywordRow } from "./newKeywords.types";
import { useNewKeywordsCard } from "./useNewKeywordsCard";

const KEY_PREFIX = `${METRIC_CARDS_KEY_PREFIX}.new.keywords`;

const RANK_COLUMN_WIDTH = 32;
const TABLE_BORDERS = { header: false, rowBorders: true, lastRowBorder: true };

const buildColumnConfig = (t: ReturnType<typeof useTranslation>): ColumnsType<INewKeywordRow> => [
    {
        key: "rank",
        title: "",
        width: RANK_COLUMN_WIDTH,
        render: (_value, _row, index) => (
            <Text
                fontVariant="caption"
                color={colorsPalettes.carbon[300]}
            >
                {index + 1}
            </Text>
        ),
    },
    {
        dataIndex: "keyword",
        title: t(`${KEY_PREFIX}.column.keyword`),
        render: (keyword: string) => (
            <Text
                as="a"
                href={getKeywordAnalysisHref(keyword)}
                fontVariant="body2"
                color={colorsPalettes.blue[400]}
                textDecoration="none"
            >
                {keyword}
            </Text>
        ),
    },
    {
        dataIndex: "volume",
        title: t(`${KEY_PREFIX}.column.volume`),
        align: "right",
        render: (volume: number) => (
            <Text
                fontVariant="body2"
                color={colorsPalettes.carbon[400]}
            >
                {abbrNumberFilter()(volume)}
            </Text>
        ),
    },
];

export const NewKeywordsCard = () => {
    const t = useTranslation();
    const { data, isLoading, isError } = useNewKeywordsCard();
    const requestQuickFilter = useKeywordsBreakdownQuickFilterIntent((state) => state.requestQuickFilter);

    const columnConfig = useMemo(() => buildColumnConfig(t), [t]);

    const handleSeeFullList = () => {
        requestQuickFilter("new");
        scrollToKeywordsBreakdown();
    };

    return (
        <MetricCard
            title={t(`${KEY_PREFIX}.title`)}
            infoText={t(`${KEY_PREFIX}.tooltip`)}
            dataAutomation="search-trends-metric-card-new-keywords"
            isEmpty={isError || (!isLoading && (!data || data.topRows.length === 0))}
        >
            <Stack flex="1">
                <Table
                    rowKey="id"
                    theme="white"
                    columnConfig={columnConfig}
                    data={data?.topRows ?? []}
                    isLoading={isLoading}
                    loaderRowCount={5}
                    bordersConfig={TABLE_BORDERS}
                    pagination={{ pageSize: 5, hideOnSinglePage: true }}
                />

                <Flex flex="1" />

                {!isLoading && data ? (
                    <Flex
                        justifyContent="flex-end"
                        padding="8px 24px 16px 24px"
                    >
                        <Flex
                            as="button"
                            alignItems="center"
                            gap="4px"
                            padding="0"
                            background="none"
                            border="none"
                            cursor="pointer"
                            onClick={handleSeeFullList}
                            data-automation="search-trends-new-keywords-see-full-list"
                        >
                            <Text
                                fontVariant="subtitle2"
                                color={colorsPalettes.blue[400]}
                            >
                                {t(`${KEY_PREFIX}.see.full.list`, {
                                    count: data.totalNewCount.toLocaleString(),
                                })}
                            </Text>
                            <ArrowDown2
                                size={16}
                                color={colorsPalettes.blue[400]}
                            />
                        </Flex>
                    </Flex>
                ) : null}
            </Stack>
        </MetricCard>
    );
};
